import { useState } from "react";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/motion";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { CONTACT, IMAGES } from "../data/site";
import { Phone, Mail, MapPin, MessageCircle, Loader2, Facebook, Instagram, Youtube } from "lucide-react";

export default function Contact() {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/enquiries", { ...form, travel_type: "Contact" });
      toast.success("Message sent! Our team will get back to you shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const field = "w-full bg-transparent border-0 border-b border-ink/20 px-0 py-3 text-ink placeholder:text-ink/40 focus:outline-none focus:border-emerald";

  return (
    <div data-testid="contact-page">
      <PageHero eyebrow="Contact" title={["Let's talk", "Kerala."]}
        subtitle="Reach out any way you like — WhatsApp, call, email or the form below. We reply fast." image={IMAGES.fishingNets} />
      <section className="max-w-[1200px] mx-auto px-5 lg:px-10 py-24 grid lg:grid-cols-2 gap-16">
        <Reveal>
          <p className="eyebrow text-gold mb-6">Get in Touch</p>
          <div className="space-y-6">
            <a href={`tel:${CONTACT.phoneRaw}`} className="flex items-center gap-4 group" data-testid="contact-phone">
              <span className="w-12 h-12 border border-emerald/20 flex items-center justify-center group-hover:bg-emerald group-hover:text-cream transition-colors"><Phone className="w-5 h-5" /></span>
              <div><p className="text-xs uppercase tracking-widest text-ink/40">Call</p><p className="text-emerald font-medium">{CONTACT.phone}</p></div>
            </a>
            <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noreferrer" className="flex items-center gap-4 group" data-testid="contact-whatsapp">
              <span className="w-12 h-12 border border-emerald/20 flex items-center justify-center group-hover:bg-emerald group-hover:text-cream transition-colors"><MessageCircle className="w-5 h-5" /></span>
              <div><p className="text-xs uppercase tracking-widest text-ink/40">WhatsApp</p><p className="text-emerald font-medium">Message us instantly</p></div>
            </a>
            <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-4 group" data-testid="contact-email">
              <span className="w-12 h-12 border border-emerald/20 flex items-center justify-center group-hover:bg-emerald group-hover:text-cream transition-colors"><Mail className="w-5 h-5" /></span>
              <div><p className="text-xs uppercase tracking-widest text-ink/40">Email</p><p className="text-emerald font-medium">{CONTACT.email}</p></div>
            </a>
            <div className="flex items-center gap-4">
              <span className="w-12 h-12 border border-emerald/20 flex items-center justify-center"><MapPin className="w-5 h-5" /></span>
              <div><p className="text-xs uppercase tracking-widest text-ink/40">Office</p><p className="text-emerald font-medium max-w-xs">{CONTACT.address}</p></div>
            </div>
          </div>
          <div className="flex gap-3 mt-8">
            {[[Facebook, CONTACT.social.facebook, "facebook"], [Instagram, CONTACT.social.instagram, "instagram"], [Youtube, CONTACT.social.youtube, "youtube"]].map(([Icon, href, key]) => (
              <a key={key} href={href} target="_blank" rel="noreferrer" data-testid={`contact-social-${key}`} className="w-11 h-11 border border-emerald/20 flex items-center justify-center hover:bg-emerald hover:text-cream transition-colors"><Icon className="w-4 h-4" /></a>
            ))}
          </div>
          <div className="mt-10 overflow-hidden border border-emerald/10 h-[280px]">
            <iframe title="Office Map" src={CONTACT.mapEmbed} width="100%" height="100%" style={{ border: 0 }} loading="lazy" data-testid="contact-map" />
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="border border-emerald/15 p-8 lg:p-10 bg-white">
            <h2 className="font-serif text-3xl font-light text-emerald mb-2">Send an Enquiry</h2>
            <p className="text-ink/50 text-sm mb-6">We'll respond within a few hours.</p>
            <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
              <input required placeholder="Full name" value={form.name} onChange={set("name")} className={field} data-testid="contact-name" />
              <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} className={field} data-testid="contact-form-email" />
              <input required placeholder="Phone / WhatsApp" value={form.phone} onChange={set("phone")} className={field} data-testid="contact-form-phone" />
              <textarea required placeholder="How can we help?" value={form.message} onChange={set("message")} className={field + " min-h-[120px]"} data-testid="contact-message" />
              <button type="submit" disabled={loading} className="w-full bg-emerald text-cream py-4 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-gold transition-colors flex items-center justify-center gap-2" data-testid="contact-submit">
                {loading && <Loader2 className="w-4 h-4 animate-spin" />} {loading ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
